import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable"; // Import the autoTable plugin

const API = "http://localhost:5000";

function currency(n) {
  const num = Number(n || 0);
  return num.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });
}

export default function Quotation() {
  const [mainCats, setMainCats] = useState([]);
  const [subs, setSubs] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedMain, setSelectedMain] = useState("");
  const [selectedSub, setSelectedSub] = useState("");
  const [selectedDetailId, setSelectedDetailId] = useState("");
  const [qty, setQty] = useState(1);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState({ mains: false, subs: false, products: false });
  const [err, setErr] = useState("");

  useEffect(() => {
    setLoading((l) => ({ ...l, mains: true }));
    axios
      .get(`${API}/api/main-categories`)
      .then((res) => setMainCats(res.data || []))
      .catch((e) => setErr(e?.response?.data?.message || e.message))
      .finally(() => setLoading((l) => ({ ...l, mains: false })));
  }, []);

  useEffect(() => {
    setSubs([]);
    setProducts([]);
    setSelectedSub("");
    setSelectedDetailId("");
    if (!selectedMain) return;

    setLoading((l) => ({ ...l, subs: true }));
    axios
      .get(`${API}/api/sub-categories/${selectedMain}`)
      .then((res) => setSubs(res.data || []))
      .catch((e) => setErr(e?.response?.data?.message || e.message))
      .finally(() => setLoading((l) => ({ ...l, subs: false })));
  }, [selectedMain]);

  useEffect(() => {
    setProducts([]);
    setSelectedDetailId("");
    if (!selectedSub) return;

    setLoading((l) => ({ ...l, products: true }));
    axios
      .get(`${API}/api/products/${selectedSub}`)
      .then((res) => setProducts(res.data || []))
      .catch((e) => setErr(e?.response?.data?.message || e.message))
      .finally(() => setLoading((l) => ({ ...l, products: false })));
  }, [selectedSub]);

  const selectedProduct = useMemo(() => {
    if (!selectedDetailId) return null;
    return products.find((p) => String(p.detail_id) === String(selectedDetailId)) || null;
  }, [selectedDetailId, products]);

  function addItem() {
    setErr("");
    if (!selectedProduct) {
      setErr("Please select a product.");
      return;
    }
    const qtyNum = Math.max(1, parseInt(qty || 1, 10));
    setItems((prev) => {
      const idx = prev.findIndex((x) => String(x.detail_id) === String(selectedProduct.detail_id));
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + qtyNum };
        return copy;
      }
      return [...prev, { ...selectedProduct, quantity: qtyNum }];
    });
  }

  function updateQty(detail_id, newQty) {
    const n = Math.max(1, parseInt(newQty || 1, 10));
    setItems((prev) => prev.map((it) => (String(it.detail_id) === String(detail_id) ? { ...it, quantity: n } : it)));
  }

  function removeItem(detail_id) {
    setItems((prev) => prev.filter((it) => String(it.detail_id) !== String(detail_id)));
  }

  const totals = useMemo(() => {
    let subtotal = 0;
    let tax = 0;
    items.forEach((it) => {
      const price = Number(it.listprice || 0);
      const q = Number(it.quantity || 0);
      const line = price * q;
      subtotal += line;
      const gst = Number(it.gstrate || 0) / 100;
      tax += line * gst;
    });
    const grand = subtotal + tax;
    return { subtotal, tax, grand };
  }, [items]);

  const downloadPDF = () => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.text("Quotation", 20, 20);
      doc.setFontSize(12);
      doc.text("Date: September 3, 2025", 20, 30);
      doc.text("Your Company Name", 20, 40);
      doc.text("Your address", 20, 45);
      doc.text("Phone: Your phone number", 20, 50);
      doc.text("Email: your.email@example.com", 20, 55);

      doc.text("To:", 20, 70);
      doc.text("Customer Name", 20, 75);
      doc.text("Customer Address", 20, 80);
      doc.text("Customer City, State, ZIP", 20, 85);
      doc.text("Email: customer.email@example.com", 20, 90);

      const tableData = items.map((it) => [
        it.product_name,
        it.batch,
        it.size || "-",
        currency(it.listprice),
        it.quantity,
        `${it.gstrate}%`,
        currency(Number(it.listprice) * Number(it.quantity)),
      ]);

      // Use autoTable from the imported plugin
      autoTable(doc, {
        startY: 100,
        head: [["Product", "Model", "Size", "Price (INR)", "Qty", "GST %", "Line Total (INR)"]],
        body: tableData,
        foot: [
          ["", "", "", "", "", "Subtotal", currency(totals.subtotal)],
          ["", "", "", "", "", "GST Total", currency(totals.tax)],
          ["", "", "", "", "", "Grand Total", currency(totals.grand)],
        ],
        theme: "grid",
        styles: { fontSize: 10 },
        headStyles: { fillColor: [200, 200, 200] },
        margin: { left: 20, right: 20 },
      });

      doc.text("Terms and Conditions:", 20, doc.lastAutoTable.finalY + 10);
      doc.text("1. Prices are inclusive of taxes unless otherwise stated.", 20, doc.lastAutoTable.finalY + 15);
      doc.text("2. Quotation is valid for 30 days from the date of issue.", 20, doc.lastAutoTable.finalY + 20);
      doc.text("3. Payment terms: 50% advance, balance on delivery.", 20, doc.lastAutoTable.finalY + 25);

      doc.save("quotation.pdf");
    } catch (error) {
      console.error("PDF generation failed:", error);
      setErr("Failed to generate PDF. Please try again.");
    }
  };

  return (
    <div className="container py-3">
      <h3 className="mb-3">Generate Quotation</h3>

      {err ? <div className="alert alert-danger py-2">{String(err)}</div> : null}

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">Main Category</label>
              <select
                className="form-select"
                value={selectedMain}
                onChange={(e) => setSelectedMain(e.target.value)}
              >
                <option value="">-- Select --</option>
                {mainCats.map((mc) => (
                  <option key={mc.id} value={mc.id}>
                    {mc.maincategory_name}
                  </option>
                ))}
              </select>
              {loading.mains && <small className="text-muted">Loading…</small>}
            </div>

            <div className="col-md-4">
              <label className="form-label">Sub Category</label>
              <select
                className="form-select"
                value={selectedSub}
                onChange={(e) => setSelectedSub(e.target.value)}
                disabled={!selectedMain}
              >
                <option value="">-- Select --</option>
                {subs.map((sc) => (
                  <option key={sc.id} value={sc.id}>
                    {sc.subcategory_name}
                  </option>
                ))}
              </select>
              {loading.subs && <small className="text-muted">Loading…</small>}
            </div>

            <div className="col-md-5">
              <label className="form-label">Product (Name • Model • Size • Price)</label>
              <select
                className="form-select"
                value={selectedDetailId}
                onChange={(e) => setSelectedDetailId(e.target.value)}
                disabled={!selectedSub || !products.length}
              >
                <option value="">-- Select --</option>
                {products.map((p) => (
                  <option key={p.detail_id} value={p.detail_id}>
                    {p.product_name} • {p.batch} • {p.size || "-"} • {currency(p.listprice)}
                  </option>
                ))}
              </select>
              {loading.products && <small className="text-muted">Loading…</small>}
            </div>

            <div className="col-md-2">
              <label className="form-label">Qty</label>
              <input
                type="number"
                className="form-control"
                min={1}
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                disabled={!selectedDetailId}
              />
            </div>

            <div className="col-md-2 d-grid">
              <button className="btn btn-primary" onClick={addItem} disabled={!selectedDetailId}>
                Add to list
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="m-0">Items</h5>
            <small className="text-muted">{items.length} item(s)</small>
          </div>

          <div className="table-responsive">
            <table className="table table-sm table-bordered align-middle">
              <thead className="table-light">
                <tr>
                  <th style={{ minWidth: 220 }}>Product</th>
                  <th>Model</th>
                  <th>Size</th>
                  <th className="text-end">Price</th>
                  <th style={{ width: 120 }}>Qty</th>
                  <th className="text-end">GST %</th>
                  <th className="text-end">Line Total</th>
                  <th style={{ width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center text-muted py-4">
                      No items added yet.
                    </td>
                  </tr>
                ) : (
                  items.map((it) => {
                    const price = Number(it.listprice || 0);
                    const qty = Number(it.quantity || 0);
                    const line = price * qty;
                    const gstPct = Number(it.gstrate || 0);
                    return (
                      <tr key={it.detail_id}>
                        <td>
                          <div className="fw-semibold">{it.product_name}</div>
                          {it.description ? <small className="text-muted">{it.description}</small> : null}
                        </td>
                        <td>{it.batch}</td>
                        <td>{it.size || "-"}</td>
                        <td className="text-end">{currency(price)}</td>
                        <td>
                          <input
                            type="number"
                            min={1}
                            className="form-control form-control-sm"
                            value={qty}
                            onChange={(e) => updateQty(it.detail_id, e.target.value)}
                          />
                        </td>
                        <td className="text-end">{gstPct}%</td>
                        <td className="text-end">{currency(line)}</td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeItem(it.detail_id)}
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              {items.length > 0 && (
                <tfoot>
                  <tr>
                    <th colSpan="6" className="text-end">
                      Subtotal
                    </th>
                    <th className="text-end">{currency(totals.subtotal)}</th>
                    <th></th>
                  </tr>
                  <tr>
                    <th colSpan="6" className="text-end">
                      GST Total
                    </th>
                    <th className="text-end">{currency(totals.tax)}</th>
                    <th></th>
                  </tr>
                  <tr>
                    <th colSpan="6" className="text-end fs-6">
                      Grand Total
                    </th>
                    <th className="text-end fs-6">{currency(totals.grand)}</th>
                    <th></th>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <div className="d-flex gap-2 justify-content-end">
            <button
              className="btn btn-outline-secondary"
              onClick={() => setItems([])}
              disabled={!items.length}
            >
              Clear
            </button>
            <button className="btn btn-success" disabled={!items.length}>
              Save Quotation
            </button>
            <button className="btn btn-primary" onClick={downloadPDF} disabled={!items.length}>
              Download PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}